import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import NavBar from '../components/Navbar/NavBar';
import { usePerformance } from '../hooks/usePerformance';
import { DecryptText, SynonymRotator } from './TextEffects';

const Hero = () => {
    const { isMobile, prefersReducedMotion } = usePerformance();
    
    // Disable parallax on mobile and reduced motion for better performance
    const { scrollY } = useScroll();
    const shouldUseParallax = !isMobile && !prefersReducedMotion;
    
    const y = useTransform(scrollY, shouldUseParallax ? [0, 400] : [0, 0], shouldUseParallax ? [0, 150] : [0, 0]);
    const opacity = useTransform(scrollY, shouldUseParallax ? [0, 300] : [0, 0], shouldUseParallax ? [1, 0] : [1, 1]);
    const scale = useTransform(scrollY, shouldUseParallax ? [0, 400] : [0, 0], shouldUseParallax ? [1, 0.92] : [1, 1]);

    return (
        <header className="hero bg-white/50 dark:bg-black/50 backdrop-blur-md min-h-screen flex flex-col relative" id='hero'>
            <NavBar />
            
            <motion.section 
                className="flex-1 flex items-center justify-center m-auto overflow-hidden mx-4 p-2 md:p-12 relative z-10"
                style={{ 
                    y, 
                    opacity, 
                    scale,
                    willChange: shouldUseParallax ? 'transform, opacity' : 'auto',
                    transform: 'translateZ(0)', // Force GPU acceleration
                }}
            >
                <div id='hero' className="max-w-7xl mx-auto w-full">
                    <motion.div 
                        className="flex flex-col justify-center text-center lg:text-left"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <motion.h1 
                            className="mb-8 md:text-7xl text-5xl font-black leading-tight tracking-tight"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <motion.span
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.25, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                                className="text-gray-900 dark:text-white"
                            >
                                Demain a déjà{' '}
                            </motion.span>
                            <br />
                            <motion.span
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.25, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                            >
                                <DecryptText 
                                    text="commencé" 
                                    className="md:text-7xl text-5xl font-black text-gray-900 dark:text-white"
                                    delay={200}
                                    speed={40}
                                />
                            </motion.span>
                        </motion.h1>
                        <motion.p 
                            className="text-xl md:text-2xl font-light tracking-wide mb-12 max-w-3xl mx-auto lg:mx-0"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <span className="text-gray-600 dark:text-gray-300">Une agence média 360 dédiée à </span>
                            <span className="text-gray-600 dark:text-gray-300 font-semibold">l'excellence</span>
                            <span className="text-gray-600 dark:text-gray-300"> et à </span>
                            <SynonymRotator 
                                synonyms={[
                                    'l\'innovation',
                                    'la créativité',
                                    'la performance',
                                    'la disruption',
                                    'la stratégie',
                                    'la pertinence',
                                    'la précision',
                                    'l\'impact',
                                    'la vision'
                                ]}
                                className="text-xl md:text-2xl font-semibold text-gray-600 dark:text-gray-300"
                                interval={2000}
                                typingSpeed={50}
                            />
                            <span className="text-gray-600 dark:text-gray-300">.</span>
                        </motion.p>
                        <motion.div 
                            className="mb-4 space-x-0 md:space-x-2 md:mb-8"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <Link 
                                to="/contact" 
                                className="text-white bg-gray-900 dark:bg-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 inline-flex items-center justify-center w-full px-8 py-4 my-4 text-lg font-semibold tracking-wide sm:w-auto sm:mb-0 transition-all duration-300 group" 
                                aria-label="En savoir plus sur Tomorrow Global"
                            >
                                En savoir plus
                                <motion.svg 
                                    className="w-5 h-5 ml-2" 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    viewBox="0 0 20 20" 
                                    fill="currentColor" 
                                    aria-hidden="true"
                                    animate={{ x: [0, 5, 0] }}
                                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                                >
                                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"></path>
                                </motion.svg>
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>
        </header>
    )
}

export default Hero;